import { Link } from '@/components/link';
import type { ShopperSearch } from '@/scapi';
import { WishlistIconButton } from '@/components/buttons/wishlist-icon-button';
import { cn } from '@/lib/utils';
import { ProductTileImageCustom } from './product-tile-image-custom';
import { ProductTileTagOverflowRow } from './product-tile-tag-overflow-row';
import { PRODUCT_TILE_CUSTOM_SAND_PLACEHOLDER } from './product-tile-custom-types';

type SearchHit = ShopperSearch.schemas['ProductSearchHit'];

export interface ProductTileCustomVerticalImageSectionProps {
    product: SearchHit;
    productUrl: string;
    effectiveImgAspectRatio: number;
    handleProductClick?: (product: SearchHit) => void;
    productHighlight?: string;
    eatingList: string[];
    /** Wishlist icon color; defaults to neutral-20 for standard PLP vertical tiles. */
    wishlistIconClassName?: string;
}

/**
 * Figma card-product / image: square media (corner-16), sand fill, tag row + wishlist inset (space-12, gap-16).
 */
export function ProductTileCustomVerticalImageSection({
    product,
    productUrl,
    effectiveImgAspectRatio,
    handleProductClick,
    productHighlight,
    eatingList,
    wishlistIconClassName = 'text-[var(--neutral-20)]',
}: ProductTileCustomVerticalImageSectionProps) {
    return (
        <div
            className={cn(
                'relative z-20 aspect-square w-full min-w-0 max-w-full overflow-hidden rounded-lg',
                PRODUCT_TILE_CUSTOM_SAND_PLACEHOLDER
            )}>
            <ProductTileImageCustom
                variant="plp-vertical"
                product={product}
                pdpLink={false}
                imgAspectRatio={effectiveImgAspectRatio}
                handleProductClick={handleProductClick}
            />
            {/** Sits under tags/wishlist; column above full-card `Link` receives pointer hits. */}
            <Link
                to={productUrl}
                tabIndex={-1}
                aria-hidden
                className="absolute inset-0 z-[1] rounded-lg"
                onClick={() => handleProductClick?.(product)}
            />
            <div className="pointer-events-none absolute inset-0 z-[5]">
                <div className="flex w-full min-w-0 items-start justify-between gap-4 p-3">
                    {/** Sale % lives next to price only; not on the image row. */}
                    <div className="pointer-events-none flex min-w-0 flex-1 flex-wrap content-start items-center gap-1">
                        <ProductTileTagOverflowRow
                            highlight={productHighlight}
                            eatingStyles={eatingList}
                            className="min-h-5"
                        />
                    </div>
                    <div className="pointer-events-auto z-20 flex shrink-1 self-start">
                        <WishlistIconButton
                            buttonVariant="small"
                            product={product}
                            className={cn('size-6 m-0', wishlistIconClassName)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
